import AppMetadataApiService from "../services/app-metadata-api.service";
import {AppMetadataDto} from "../models/interfaces/app-metadata-dto.interface";
import {UpdateThemingDto} from "../models/interfaces/update-theming-dto.interface";
import {ThemingResponseDto} from "../models/interfaces/theming-response-dto.interface";
import {FileDto} from "../models/interfaces/file-dto.interface";
import {useLoading} from "./use-loading.hook";
import {LoadingKeys} from "../models/enums/loading-keys.enum";
import {UpdateRequiredDuringVisitBody} from "../models/interfaces/update-required-during-visit-body.interface";
import {UpdateOptionalDuringVisitBody} from "../models/interfaces/update-optional-during-visit-body.interface";
import {DuringVisitInfo} from "../models/interfaces/during-visit-info.interface";

export const useAppMetadataApi = () => {
    const loadingKey = LoadingKeys.USE_APP_METADATA_API;
    const {startLoading, stopLoading} = useLoading();

    const getAppMetadata = async (): Promise<AppMetadataDto> => {
        startLoading([loadingKey, "getAppMetadata"]);
        try {
            return await AppMetadataApiService.getAppMetadata();
        } catch (err: any) {
            throw err;
        } finally {
            stopLoading([loadingKey, "getAppMetadata"]);
        }
    };

    const updateTheming = async (data: UpdateThemingDto): Promise<ThemingResponseDto> => {
        startLoading([loadingKey, "updateTheming"]);
        try {
            return await AppMetadataApiService.updateTheming(data);
        } catch (err: any) {
            throw err;
        } finally {
            stopLoading([loadingKey, "updateTheming"]);
        }
    };

    const toggleVisibility = async (): Promise<void> => {
        startLoading([loadingKey, "toggleVisibility"]);
        try {
            return await AppMetadataApiService.toggleVisibility();
        } catch (err: any) {
            throw err;
        } finally {
            stopLoading([loadingKey, "toggleVisibility"]);
        }
    };

    const getWidgetImage = async (): Promise<FileDto> => {
        startLoading([loadingKey, "getWidgetImage"]);
        try {
            return await AppMetadataApiService.getWidgetImage();
        } catch (err: any) {
            throw err;
        } finally {
            stopLoading([loadingKey, "getWidgetImage"]);
        }
    };

    const uploadWidgetImage = async (file: File): Promise<void> => {
        startLoading([loadingKey, "uploadWidgetImage"]);
        try {
            return await AppMetadataApiService.uploadWidgetImage(file);
        } catch (err: any) {
            throw err;
        } finally {
            stopLoading([loadingKey, "uploadWidgetImage"]);
        }
    };

    const updateRequiredDuringVisit = async (body: UpdateRequiredDuringVisitBody): Promise<void> => {
        startLoading([loadingKey, "updateRequiredDuringVisit"]);
        try {
            return await AppMetadataApiService.updateRequiredDuringVisit(body);
        } catch (err: any) {
            throw err;
        } finally {
            stopLoading([loadingKey, "updateRequiredDuringVisit"]);
        }
    };

    const updateOptionalDuringVisit = async (body: UpdateOptionalDuringVisitBody): Promise<void> => {
        startLoading([loadingKey, "updateOptionalDuringVisit"]);
        try {
            return await AppMetadataApiService.updateOptionalDuringVisit(body);
        } catch (err: any) {
            throw err;
        } finally {
            stopLoading([loadingKey, "updateOptionalDuringVisit"]);
        }
    };

    const getDuringVisitInfo = async (): Promise<DuringVisitInfo> => {
        startLoading([loadingKey, "getDuringVisitInfo"]);
        try {
            return await AppMetadataApiService.getDuringVisitInfo();
        } catch (err: any) {
            throw err;
        } finally {
            stopLoading([loadingKey, "getDuringVisitInfo"]);
        }
    };

    return {
        getAppMetadata,
        updateTheming,
        toggleVisibility,
        getWidgetImage,
        uploadWidgetImage,
        updateRequiredDuringVisit,
        updateOptionalDuringVisit,
        getDuringVisitInfo,
    };
};

